export default class HeroBorderDrawer {
    constructor(container) {
        this.div = container;

        this.thickLineConfigs = {
            path1: {
                coefficient: 0.3,        // How much of current drawn length to use
                maxLength: 100,          // Maximum length in pixels
                shrinkStartPercent: 80,  // When to start shrinking
                minEndLength: 20         // Minimum length at 100% progress
            },
            path2: {
                coefficient: 0.4,        // Different coefficient for second path
                maxLength: 120,          // Different max length
                shrinkStartPercent: 70,  // Earlier shrink start
                minEndLength: 30         // Different min length
            }
        };

        // Create SVG elements
        this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        this.path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.path1Thick = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.path2Thick = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        // Set up SVG attributes
        this.svg.setAttribute('width', '100%');
        this.svg.setAttribute('height', '100%');
        this.svg.setAttribute('preserveAspectRatio', 'none');
        this.svg.style.position = 'absolute';
        this.svg.style.top = '0';
        this.svg.style.left = '0';
        this.svg.style.pointerEvents = 'none';
        this.svg.classList.add('hero-border-drawer');

        // Set up thin paths attributes (white)
        this.path1.setAttribute('fill', 'none');
        this.path1.setAttribute('stroke', '#74a417');
        this.path1.setAttribute('stroke-width', '1');
        this.path1.setAttribute('stroke-opacity', '1');
        this.path1.style.paintOrder = 'stroke';
        this.path1.style.zIndex = '2';

        this.path2.setAttribute('fill', 'none');
        this.path2.setAttribute('stroke', '#74a417');
        this.path2.setAttribute('stroke-width', '1');
        this.path2.setAttribute('stroke-opacity', '1');
        this.path2.style.paintOrder = 'stroke';
        this.path2.style.zIndex = '2';

// Set up thick paths attributes (black)
        this.path1Thick.setAttribute('fill', 'none');
        this.path1Thick.setAttribute('stroke', '#021B28');
        this.path1Thick.setAttribute('stroke-width', '1');
        this.path1Thick.setAttribute('stroke-opacity', '1');
        this.path1Thick.style.paintOrder = 'stroke';
        this.path1Thick.style.zIndex = '1';

        this.path2Thick.setAttribute('fill', 'none');
        this.path2Thick.setAttribute('stroke', '#021B28');
        this.path2Thick.setAttribute('stroke-width', '1');
        this.path2Thick.setAttribute('stroke-opacity', '1');
        this.path2Thick.style.paintOrder = 'stroke';
        this.path2Thick.style.zIndex = '1';

        // Append elements in order (thin paths first, thick paths on top)
        this.svg.appendChild(this.path1);
        this.svg.appendChild(this.path2);
        this.svg.appendChild(this.path1Thick);
        this.svg.appendChild(this.path2Thick);


        this.div.appendChild(this.svg);

        this.padding = this.getPadding();

        // Store path lengths for animation
        this.pathLengths = {
            path1: 0,
            path2: 0,
            path1Thick: 0,
            path2Thick: 0
        };

        // Initialize
        this.init();
    }

    getPadding() {
        return {
            y: 30,
            x: 40,
        };
    }

    updatePaths() {
        const rect = this.div.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        // First path: bottom-left -> bottom-right -> top-right
        const d1 = `M 0,${height - this.padding.y} 
                    L ${width - this.padding.x},${height - this.padding.y} 
                    L ${width - this.padding.x},0`;

        // Second path: bottom-left -> top-left -> top-right
        const d2 = `M ${this.padding.x},${height} 
                    L ${this.padding.x},${this.padding.y} 
                    L ${width},${this.padding.y}`;

        // Set paths for both thin and thick versions
        this.path1.setAttribute('d', d1);
        this.path2.setAttribute('d', d2);
        this.path1Thick.setAttribute('d', d1);
        this.path2Thick.setAttribute('d', d2);

        // Update viewBox to match container dimensions
        this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

        // Store path lengths for animation
        this.pathLengths.path1 = this.path1.getTotalLength();
        this.pathLengths.path2 = this.path2.getTotalLength();
        this.pathLengths.path1Thick = this.path1Thick.getTotalLength();
        this.pathLengths.path2Thick = this.path2Thick.getTotalLength();

        // Set initial state for all paths
        this.path1.style.strokeDasharray = `${this.pathLengths.path1} ${this.pathLengths.path1}`;
        this.path2.style.strokeDasharray = `${this.pathLengths.path2} ${this.pathLengths.path2}`;
        this.path1Thick.style.strokeDasharray = `${this.pathLengths.path1Thick} ${this.pathLengths.path1Thick}`;
        this.path2Thick.style.strokeDasharray = `${this.pathLengths.path2Thick} ${this.pathLengths.path2Thick}`;

        // Reset to invisible state
        this.update(0);
    }

    calculateDesiredLength(totalLength, percent, pathConfig) {
        // Calculate base desired length using path-specific config
        const baseLength = Math.min(
            totalLength * pathConfig.coefficient * percent / 100,
            pathConfig.maxLength
        );

        // If we haven't reached shrink point, return base length
        if (percent <= pathConfig.shrinkStartPercent) {
            return baseLength;
        }

        // Calculate how far we are in the shrinking phase (0 to 1)
        const shrinkProgress = (percent - pathConfig.shrinkStartPercent) /
            (100 - pathConfig.shrinkStartPercent);

        // Use easeOutQuad for smoother shrinking
        const easedShrinkProgress = 1 - Math.pow(1 - shrinkProgress, 2);

        // Shrink the line length but keep minimum length
        const shrinkAmount = baseLength - pathConfig.minEndLength;
        return baseLength - (shrinkAmount * easedShrinkProgress);
    }

    updateThickLine(path, pathLength, mainPathOffset, totalLength, percent, configKey) {
        // Get the configuration for this specific path
        const pathConfig = this.thickLineConfigs[configKey];

        // Calculate desired length with path-specific configuration
        const desiredLength = this.calculateDesiredLength(totalLength, percent, pathConfig);

        // Set dash pattern: visible segment of desiredLength followed by invisible segment
        path.style.strokeDasharray = `${desiredLength} ${pathLength}`;

        // Offset pattern by mainPathOffset plus twice the desiredLength
        path.style.strokeDashoffset = mainPathOffset + desiredLength * 2;
    }

    update(percent) {
        // Ensure percent is between 0 and 100
        const progress = Math.max(0, Math.min(100, percent)) / 100;

        // Calculate the offset for regular paths
        const offset1 = this.pathLengths.path1 * (1 - progress);
        const offset2 = this.pathLengths.path2 * (1 - progress);

        // Update regular paths
        this.path1.style.strokeDashoffset = offset1;
        this.path2.style.strokeDashoffset = offset2;

        // Update thick paths with their specific configurations
        this.updateThickLine(
            this.path1Thick,
            this.pathLengths.path1Thick,
            offset1,
            this.pathLengths.path1,
            percent,
            'path1'
        );
        this.updateThickLine(
            this.path2Thick,
            this.pathLengths.path2Thick,
            offset2,
            this.pathLengths.path2,
            percent,
            'path2'
        );
    }

    init() {
        this.updatePaths();
    }

    resize() {
        this.updatePaths();
    }

    destroy() {
        window.removeEventListener('resize', this.updatePaths);
    }
}
